import React, { useState, useEffect } from 'react';
import { groupSkillsBySource, skillExists } from '../utils/skillsMergeLogic';

const EditableSkills = ({ 
  onSkillsChange, 
  initialSkills = [], 
  readOnly = false 
}) => {
  const [skills, setSkills] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [isAddingSkill, setIsAddingSkill] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch skills from the new alumni_skills table
  const fetchSkills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const url = `${backendUrl}/api/alumni/skills`;
      
      console.log('Fetching skills from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch skills: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Skills data received:', data);
      setSkills(data.skills || []);
      setError('');
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError(`Failed to load skills: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add a new manual skill
  const addSkill = async () => {
    if (!newSkillName.trim()) {
      setError('Please enter a skill name');
      return;
    }

    // Check if skill already exists (case-insensitive)
    if (skillExists(skills, newSkillName)) {
      setError('This skill already exists');
      return;
    }

    try {
      setIsAddingSkill(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/skills/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          skill_name: newSkillName.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add skill');
      }

      setNewSkillName('');
      setSuccessMessage(`Added "${newSkillName.trim()}" successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh skills list
      await fetchSkills();
      
      // Notify parent component
      if (onSkillsChange) {
        onSkillsChange();
      }
    } catch (err) {
      console.error('Error adding skill:', err);
      setError(err.message);
    } finally {
      setIsAddingSkill(false);
    }
  };

  // Delete a skill (soft delete)
  const deleteSkill = async (skillId, skillName) => {
    if (!window.confirm(`Are you sure you want to remove "${skillName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/skills/${skillId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete skill');
      }

      setSuccessMessage(`Removed "${skillName}" successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh skills list
      await fetchSkills();
      
      // Notify parent component
      if (onSkillsChange) {
        onSkillsChange();
      }
    } catch (err) {
      console.error('Error deleting skill:', err);
      setError(err.message);
    }
  };

  // Load skills on component mount
  useEffect(() => {
    fetchSkills();
  }, []);

  // Handle Enter key for adding skills
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isAddingSkill) {
      addSkill();
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Skills</h3>
        <div className="animate-pulse">
          <div className="flex flex-wrap gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded-full w-20"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Group skills by source for better organization
  const groupedSkills = groupSkillsBySource(skills);
  const { linkedin, manual, all } = groupedSkills;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Skills</h3>
        {!readOnly && (
          <div className="text-xs text-gray-500">
            Click × to remove • Enter to add new
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-md text-sm">
          {successMessage}
        </div>
      )}

      {/* LinkedIn Skills (Endorsed) */}
      {linkedin.filter(skill => skill.is_endorsed).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            LinkedIn Endorsed Skills ({linkedin.filter(skill => skill.is_endorsed).length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {linkedin
              .filter(skill => skill.is_endorsed)
              .sort((a, b) => b.endorsement_count - a.endorsement_count)
              .map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {skill.skill_name}
                <span className="ml-1 text-xs bg-blue-200 text-blue-700 px-1.5 py-0.5 rounded-full">
                  {skill.endorsement_count}
                </span>
                <span className="ml-1 text-xs text-blue-600">
                  (LinkedIn)
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* LinkedIn Skills (Unendorsed) */}
      {linkedin.filter(skill => !skill.is_endorsed).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            LinkedIn Skills ({linkedin.filter(skill => !skill.is_endorsed).length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {linkedin
              .filter(skill => !skill.is_endorsed)
              .map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {skill.skill_name}
                <span className="ml-1 text-xs text-blue-600">
                  (LinkedIn)
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Manual Skills */}
      {manual.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Your Skills ({manual.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {manual.map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 group relative"
              >
                {skill.skill_name}
                {!readOnly && (
                  <button
                    onClick={() => deleteSkill(skill.id, skill.skill_name)}
                    className="ml-2 text-blue-600 hover:text-red-600 hover:bg-red-50 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold opacity-60 hover:opacity-100 transition-all duration-200"
                    title="Remove skill"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add New Skill Input */}
      {!readOnly && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Add New Skill
          </h4>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter skill name..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isAddingSkill}
            />
            <button
              onClick={addSkill}
              disabled={isAddingSkill || !newSkillName.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingSkill ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* No Skills Message */}
      {all.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No skills added yet.</p>
          {!readOnly && (
            <p className="text-xs mt-1">Add your first skill above to get started!</p>
          )}
        </div>
      )}

      {/* Summary */}
      {all.length > 0 && (
        <div className="text-xs text-gray-500">
          Total: {all.length} skills 
          ({linkedin.filter(skill => skill.is_endorsed).length} LinkedIn endorsed, 
           {linkedin.filter(skill => !skill.is_endorsed).length} LinkedIn unendorsed, 
           {manual.length} manually added)
        </div>
      )}
    </div>
  );
};

export default EditableSkills;